import React, { useMemo } from 'react';
import { Rect, Group, Circle, Text, Line, Arc } from 'react-konva';
import { trimShapesAgainstCylinders } from '../../utils/lineTrimming';
import { repairFastenerProfileContour, hideFastenerHolesUnderClamps } from '../../utils/fastenerTransform';
import fastenersData from '../fastenersData';

// Пересечение двух прямоугольников при evenodd снова включает сплошную линию.
// Режем накрытие на клетки без наложений, чтобы скрытая часть оставалась скрытой.
function disjointOccluders(rects) {
  const xs = [];
  const ys = [];
  rects.forEach((rect) => {
    xs.push(rect.x, rect.x + rect.width);
    ys.push(rect.y, rect.y + rect.height);
  });
  const unique = (values) => [...new Set(values.map((value) => Math.round(value * 1000) / 1000))].sort((a, b) => a - b);
  const xEdges = unique(xs);
  const yEdges = unique(ys);
  const cells = [];
  for (let i = 0; i < xEdges.length - 1; i += 1) {
    for (let j = 0; j < yEdges.length - 1; j += 1) {
      const x = xEdges[i];
      const y = yEdges[j];
      const width = xEdges[i + 1] - x;
      const height = yEdges[j + 1] - y;
      if (width < 0.05 || height < 0.05) continue;
      const cx = x + width / 2;
      const cy = y + height / 2;
      const covered = rects.some((rect) => (
        cx > rect.x && cx < rect.x + rect.width && cy > rect.y && cy < rect.y + rect.height
      ));
      if (covered) cells.push({ x, y, width, height });
    }
  }
  return cells;
}

function fittingLayoutOf(item) {
  let scaleX = 1;
  let scaleY = 1;
  let contentOffsetX = 0;
  let contentOffsetY = 0;
  const width = Number(item.width) || 0;
  const height = Number(item.height) || 0;
  const originalWidth = Number(item.originalWidth) || 0;
  const originalHeight = Number(item.originalHeight) || 0;
  if (item.useOriginalSize) {
    if (originalWidth && width) contentOffsetX = (width - originalWidth) / 2;
    if (originalHeight && height) {
      contentOffsetY = typeof item.correctionOffset === 'number'
        ? ((height - originalHeight) / 4) + item.correctionOffset
        : (height - originalHeight) / 2;
    }
  } else if (originalWidth && originalHeight) {
    scaleX = width / originalWidth;
    scaleY = height / originalHeight;
  }
  return { scaleX, scaleY, contentOffsetX, contentOffsetY };
}

function localToWorld(item, lx, ly) {
  const rot = ((Number(item.rotation) || 0) * Math.PI) / 180;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const gx = Number(item.groupX) || 0;
  const gy = Number(item.groupY) || 0;
  const ox = Number(item.offsetX) || 0;
  const oy = Number(item.offsetY) || 0;
  const px = (Number(item.x) || 0) + lx;
  const py = (Number(item.y) || 0) + ly;
  const dx = px - ox;
  const dy = py - oy;
  return { x: gx + dx * cos - dy * sin, y: gy + dx * sin + dy * cos };
}

function worldToShape(item, layout, wx, wy) {
  const rot = ((Number(item.rotation) || 0) * Math.PI) / 180;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const gx = Number(item.groupX) || 0;
  const gy = Number(item.groupY) || 0;
  const ox = Number(item.offsetX) || 0;
  const oy = Number(item.offsetY) || 0;
  const dx = wx - gx;
  const dy = wy - gy;
  const lx = dx * cos + dy * sin + ox;
  const ly = -dx * sin + dy * cos + oy;
  return {
    x: (lx - (Number(item.x) || 0) - layout.contentOffsetX) / (layout.scaleX || 1),
    y: (ly - (Number(item.y) || 0) - layout.contentOffsetY) / (layout.scaleY || 1),
  };
}

function boundsOf(points) {
  const left = Math.min(...points.map((point) => point.x));
  const top = Math.min(...points.map((point) => point.y));
  const right = Math.max(...points.map((point) => point.x));
  const bottom = Math.max(...points.map((point) => point.y));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function boxesOverlap(a, b) {
  return a && b
    && a.x < b.x + b.width && a.x + a.width > b.x
    && a.y < b.y + b.height && a.y + a.height > b.y;
}

function itemWorldBox(item) {
  const w = Number(item.width) || 0;
  const h = Number(item.height) || 0;
  return boundsOf([
    localToWorld(item, 0, 0),
    localToWorld(item, w, 0),
    localToWorld(item, w, h),
    localToWorld(item, 0, h),
  ]);
}

// Сколько 1 1/4" торчит из Ø48,3. На минимуме GS-03 это 5 мм, поэтому муфта 22 мм
// заходит на наружную трубу. Смещение диапазона меняет длину Ø48,3 и этот выступ.
function innerPipeProtrusion(fastener, info) {
  const catalogStick = fastener.itemKey === 'KRNS-GS-04' ? 40 : 5;
  const typed = (Number(fastener.fastenerHeight) || 0) + 22;
  const offset = Number(fastener.rangeOffset) || 0;
  return catalogStick - offset + typed - (Number(info.minHeight) || 0);
}

// Труба Ø48,3. y = 0 — верх стойки; отрицательный y заходит в муфту над стойкой.
function outerPipeLocalRect(fastener) {
  const info = fastenersData[fastener.itemKey];
  const lower = Number(info?.lowerPipeLength) || 0;
  if (!fastener.isFloorFastener || lower <= 0) return null;
  const h = Number(fastener.height) || 0;
  const w = Number(fastener.width) || 0;
  const clearance = Number(info.pipeFloorClearance) || 85;
  const openY = h - clearance;
  const topY = -22 + innerPipeProtrusion(fastener, info);
  const half = 24.15;
  if (topY >= openY) return null;
  return { x: w / 2 - half, y: topY, width: half * 2, height: openY - topY };
}

function outerPipeWalls(shapes) {
  const verticals = [];
  (shapes || []).forEach((shape) => {
    if (!shape || (shape.type !== 'line' && shape.type !== 'polyline') || !shape.points) return;
    const pts = shape.points;
    for (let i = 0; i + 3 < pts.length; i += 2) {
      const x1 = pts[i];
      const y1 = pts[i + 1];
      const x2 = pts[i + 2];
      const y2 = pts[i + 3];
      if (Math.abs(x2 - x1) > 1.2) continue;
      const top = Math.min(y1, y2);
      const bottom = Math.max(y1, y2);
      if (bottom - top < 40) continue;
      verticals.push({ x: (x1 + x2) / 2, top, bottom });
    }
  });
  let best = null;
  for (let i = 0; i < verticals.length; i += 1) {
    for (let j = i + 1; j < verticals.length; j += 1) {
      const gap = Math.abs(verticals[j].x - verticals[i].x);
      if (gap < 46 || gap > 52) continue;
      const score = Math.abs(gap - 48.3);
      if (!best || score < best.score) {
        const left = verticals[i].x < verticals[j].x ? verticals[i] : verticals[j];
        const right = verticals[i].x < verticals[j].x ? verticals[j] : verticals[i];
        best = { score, left, right };
      }
    }
  }
  return best;
}

function outerPipeWorldBox(fastener) {
  const pipe = outerPipeLocalRect(fastener);
  if (!pipe || pipe.height < 1) return null;
  return boundsOf([
    localToWorld(fastener, pipe.x, pipe.y),
    localToWorld(fastener, pipe.x + pipe.width, pipe.y),
    localToWorld(fastener, pipe.x, pipe.y + pipe.height),
    localToWorld(fastener, pipe.x + pipe.width, pipe.y + pipe.height),
  ]);
}

/**
 * ФУНКЦИЯ renderShape (из вашего Canvas.js)
 * Если у вас она уже лежит в utils/dxfHelpers.js, 
 * то импортируйте оттуда. Иначе можно положить здесь.
 */
function renderShape(shape, idx, dash, opacity = 1) {
  const strokeProps = {
    stroke: '#000000',
    strokeWidth: 1,
    strokeScaleEnabled: false,
    listening: false,
    opacity,
    dash,
  };
  if (shape.type === 'line' || shape.type === 'polyline') {
    return (
      <Line
        key={idx}
        points={shape.points}
        closed={shape.closed}
        {...strokeProps}
      />
    );
  } else if (shape.type === 'circle') {
    return (
      <Circle
        key={idx}
        x={shape.x}
        y={shape.y}
        radius={shape.radius}
        {...strokeProps}
      />
    );
  } else if (shape.type === 'arc') {
    // Преобразуем углы из радиан в градусы
    const startAngleDeg = (shape.startAngle * 180) / Math.PI;
    const endAngleDeg = (shape.endAngle * 180) / Math.PI;
    
    // Вычисляем угол дуги и начальную ротацию
    // В DXF углы идут против часовой стрелки, в Konva Arc тоже
    let angleDeg = endAngleDeg - startAngleDeg;
    
    // Нормализуем угол (если endAngle < startAngle, добавляем 360)
    if (angleDeg < 0) {
      angleDeg += 360;
    }
    
    return (
      <Arc
        key={idx}
        x={shape.x}
        y={shape.y}
        innerRadius={shape.radius}
        outerRadius={shape.radius}
        angle={angleDeg}
        rotation={startAngleDeg}
        {...strokeProps}
      />
    );
  }
  return null;
}

/**
 * Утилиты getFittingWidth / getFittingHeight из вашего fittingsData
 * Вы можете просто использовать fitting.width и fitting.height, 
 * но раз в коде есть такие функции, включим их:
 */
function getFittingWidth(fitting) {
  return fitting.width;
}
function getFittingHeight(fitting) {
  return fitting.height;
}

/**
 * Функция для получения точек по периметру фитинга (аналог getCylinderPoints),
 * чтобы рендерить точки под dimension line.
 */
function getFittingPoints({ x, y, width, height, snapPoints, shapes, includeHidden = false }) {
  // Если snapPoints - это функция (для крепежей с фильтрацией по слоям)
  if (typeof snapPoints === 'function') {
    const points = snapPoints(x, y, width, height, shapes);
    return points.map(point => {
      if (point.absolute) {
        return { 
          x: x + (point.x || 0), 
          y: y + (point.y || 0) 
        };
      } else if (point.relative) {
        return { 
          x: x + width * point.x, 
          y: y + height * point.y 
        };
      } else {
        return { 
          x: x + (point.x || 0), 
          y: y + (point.y || 0) 
        };
      }
    });
  }
  
  // Если у фитинга есть кастомные точки привязки (массив), используем их
  if (snapPoints && Array.isArray(snapPoints)) {
    // Фильтруем скрытые точки, только если includeHidden === false
    const filteredPoints = includeHidden 
      ? snapPoints 
      : snapPoints.filter(point => !point.hidden);
    
    // ВАЖНО: Точки привязки уже трансформированы при загрузке в Canvas.js!
    // Здесь мы их НЕ трансформируем повторно.
    
    return filteredPoints.map(point => {
      if (typeof point === 'function') {
        return point(x, y, width, height);
      } else if (point.relative) {
        return { 
          x: x + width * point.x, 
          y: y + height * point.y 
        };
      } else if (point.absolute) {
        // Абсолютные координаты в мм (например, {x: 35, y: 35, absolute: true})
        return { 
          x: x + (point.x || 0), 
          y: y + (point.y || 0) 
        };
      } else {
        return { 
          x: x + (point.x || 0), 
          y: y + (point.y || 0) 
        };
      }
    });
  }
  
  // Стандартные 8 точек по умолчанию
  return [
    { x, y },                           // top-left
    { x: x + width, y },                // top-right
    { x, y: y + height },               // bottom-left
    { x: x + width, y: y + height },    // bottom-right
    { x: x + width / 2, y },            // top-middle
    { x: x + width / 2, y: y + height },// bottom-middle
    { x, y: y + height / 2 },           // left-middle
    { x: x + width, y: y + height / 2 },// right-middle
  ];
}

/**
 * Компонент, который полностью рендерит "Fitting".
 * Здесь мы используем тот же код, что у вас в Canvas.js 
 * при рендере fittings.map(...).
 */
function FittingComponent({
  fitting,
  selectedFittingId,
  setSelectedFittingId,
  setSelectedCylinderId,
  setSelectedDimensionId,
  setSelectedBalloonId,
  highlightedSide,
  setHighlightedSide,
  highlightedPoint,
  setHighlightedPoint,
  isDimensionLineEnabled,
  snappingMode,

  isLKeyPressed,
  isPKeyPressed = false,
  pAttachmentMode = 'off', // 'off', 'points', 'edges'
  isSelected,
  isTransforming,

  // Ниже - коллбеки, которые вызывались у вас в Canvas:
  handleDragStart,
  handleDragMove,
  handleDragEnd,
  handleResizeStart,
  handleFittingResize,
  handleResizeEnd,
  handleMouseOver,
  handleSnapPointClick,
  handleEdgeClick,
  pointAttachmentMode,
  isPointConstrained,
  isEdgeConstrained,
  handleRemoveConstraint,
  handleRemoveEdgeConstraint

  // На случай, если нужно знать, зафиксирован объект или нет
  // обычно оно лежит внутри fitting.locked, 
  // но можно пробросить напрямую
  , zoom = 1,
  isPatternSelected = false
  , onSelectFitting
  , getSnappedPosition
  , cylinders = [] // Список цилиндров (труб) для обрезки линий крепежа
  , sceneFittings = []
}) {
  // <-- ВАШ КОД
  const {
    id,
    x,
    y,
    width,
    height,
    locked,
    shapes = [],
    originalWidth,
    originalHeight,
    useOriginalSize,
  } = fitting;

  // Масштабирование по X/Y
  // Если useOriginalSize=true, мы не масштабируем (scale=1), 
  // но вычисляем смещение для центрирования содержимого относительно width/height
  let scaleY = 1;
  let scaleX = 1;
  let contentOffsetX = 0;
  let contentOffsetY = 0;

  if (useOriginalSize) {
    // Не масштабируем, но центрируем
    scaleX = 1;
    scaleY = 1;
    if (originalWidth && width) {
      contentOffsetX = (width - originalWidth) / 2;
    }
    if (originalHeight && height) {
      if (typeof fitting.correctionOffset === 'number') {
        contentOffsetY = ((height - originalHeight) / 4) + fitting.correctionOffset;
      } else {
        // Fallback or default behavior if needed
        contentOffsetY = (height - originalHeight) / 2;
      }
    }
  } else {
    // Стандартное масштабирование
    scaleY = height / originalHeight;
    scaleX = width / originalWidth;
  }

  // Обрабатываем shapes крепежа: обрезка линий
  // Используем useMemo для оптимизации - пересчитываем только при изменении зависимостей
  const processedShapes = useMemo(() => {
    let currentShapes = shapes;

    // В DXF верхняя кромка планки оборвана на хомуте. Если хомут короче и за неё не выходит,
    // дорисовываем кромку, иначе контур планки остаётся с дырой.
    if (fitting.type === 'fastener' && !fitting.isFloorFastener) {
      currentShapes = repairFastenerProfileContour(currentShapes);
      currentShapes = hideFastenerHolesUnderClamps(currentShapes);
    }
    
    // ВАЖНО: Трансформация shapes для крепежей уже применена в Canvas.js при загрузке!
    // Здесь мы НЕ применяем трансформацию повторно, только обрезку линий
    
    // Планка и отверстия крепежа сзади изделия: режем и по трубам, и по фитингам.
    // Муфта сверху напольного крепежа за трубой Ø48,3, поэтому её из накрывающих убираем.
    const standPipe = fitting.type === 'fastener' ? outerPipeWorldBox(fitting) : null;
    const covers = [
      ...(cylinders || []),
      ...(sceneFittings || []).filter((item) => (
        item
        && item.id !== fitting.id
        && item.type !== 'fastener'
        && !item.isHidden
        && !(standPipe && item.itemKey === 'Mufta_1_1.4' && boxesOverlap(standPipe, itemWorldBox(item)))
      )),
    ];
    if (fitting.type === 'fastener' && covers.length > 0) {
      const fRot = ((Number(fitting.rotation) || 0) * Math.PI) / 180;
      const fCos = Math.cos(fRot);
      const fSin = Math.sin(fRot);
      const fGx = Number(fitting.groupX) || 0;
      const fGy = Number(fitting.groupY) || 0;
      const fOx = Number(fitting.offsetX) || 0;
      const fOy = Number(fitting.offsetY) || 0;
      const toShape = (wx, wy) => {
        const dx = wx - fGx;
        const dy = wy - fGy;
        const lx = dx * fCos + dy * fSin + fOx;
        const ly = -dx * fSin + dy * fCos + fOy;
        return {
          x: (lx - x - contentOffsetX) / (scaleX || 1),
          y: (ly - y - contentOffsetY) / (scaleY || 1),
        };
      };
      const localCovers = covers.map((item) => {
        const rot = ((Number(item.rotation) || 0) * Math.PI) / 180;
        const cos = Math.cos(rot);
        const sin = Math.sin(rot);
        const gx = Number(item.groupX) || 0;
        const gy = Number(item.groupY) || 0;
        const ox = Number(item.offsetX) || 0;
        const oy = Number(item.offsetY) || 0;
        const x0 = Number(item.x) || 0;
        const y0 = Number(item.y) || 0;
        const w = Number(item.width) || 0;
        const h = Number(item.height) || 0;
        const corners = [[x0, y0], [x0 + w, y0], [x0 + w, y0 + h], [x0, y0 + h]].map(([px, py]) => {
          const dx = px - ox;
          const dy = py - oy;
          return toShape(gx + dx * cos - dy * sin, gy + dx * sin + dy * cos);
        });
        const left = Math.min(...corners.map((point) => point.x));
        const top = Math.min(...corners.map((point) => point.y));
        const right = Math.max(...corners.map((point) => point.x));
        const bottom = Math.max(...corners.map((point) => point.y));
        return { x: left, y: top, width: right - left, height: bottom - top };
      }).filter((rect) => rect.width > 0.2 && rect.height > 0.2);

      currentShapes = trimShapesAgainstCylinders(currentShapes, localCovers, 'FASTENER');
    }

    // Муфта 1 1/4" напольного крепежа остаётся на своём месте, но за трубой Ø48,3:
    // её линии внутри трубы убираем, и труба рисуется поверх.
    if (fitting.itemKey === 'Mufta_1_1.4') {
      const layout = fittingLayoutOf(fitting);
      const myBox = itemWorldBox(fitting);
      const pipeCovers = (sceneFittings || []).flatMap((other) => {
        if (!other || other.id === fitting.id || !other.isFloorFastener) return [];
        const pipe = outerPipeLocalRect(other);
        if (!pipe) return [];
        const walls = outerPipeWalls(other.shapes);
        if (walls) {
          pipe.x = Math.min(walls.left.x, walls.right.x) - 0.6;
          pipe.width = Math.abs(walls.right.x - walls.left.x) + 1.2;
        }
        const pipeWorld = boundsOf([
          localToWorld(other, pipe.x, pipe.y),
          localToWorld(other, pipe.x + pipe.width, pipe.y),
          localToWorld(other, pipe.x, pipe.y + pipe.height),
          localToWorld(other, pipe.x + pipe.width, pipe.y + pipe.height),
        ]);
        if (!boxesOverlap(myBox, pipeWorld)) return [];
        const corners = [
          [pipe.x, pipe.y],
          [pipe.x + pipe.width, pipe.y],
          [pipe.x, pipe.y + pipe.height],
          [pipe.x + pipe.width, pipe.y + pipe.height],
        ].map(([px, py]) => {
          const world = localToWorld(other, px, py);
          return worldToShape(fitting, layout, world.x, world.y);
        });
        const rect = boundsOf(corners);
        return rect.width > 0.2 && rect.height > 0.2 ? [rect] : [];
      });
      if (pipeCovers.length > 0) {
        currentShapes = trimShapesAgainstCylinders(currentShapes, pipeCovers);
      }
    }

    // Стенки Ø48,3 дорисовываем вверх до её настоящего торца, в том числе внутрь муфты.
    if (fitting.type === 'fastener' && fitting.isFloorFastener) {
      const pipe = outerPipeLocalRect(fitting);
      const walls = outerPipeWalls(currentShapes);
      if (pipe && walls && pipe.y < 0 && pipe.y < Math.max(walls.left.top, walls.right.top) - 0.5) {
        const top = pipe.y;
        const x1 = walls.left.x;
        const x2 = walls.right.x;
        const extra = [];
        if (walls.left.top > top + 0.5) {
          extra.push({ type: 'line', points: [x1, walls.left.top, x1, top], scalable: true, layer: 'FASTENER' });
        }
        if (walls.right.top > top + 0.5) {
          extra.push({ type: 'line', points: [x2, walls.right.top, x2, top], scalable: true, layer: 'FASTENER' });
        }
        extra.push({ type: 'line', points: [x1, top, x2, top], scalable: true, layer: 'FASTENER' });
        currentShapes = [...currentShapes, ...extra];
      }
    }
    
    return currentShapes;
  }, [shapes, cylinders, sceneFittings, fitting, x, y, scaleX, scaleY, contentOffsetX, contentOffsetY]);

  // Задний выход прячется за всем, что на листе ближе: трубы и фитинги в плоскости чертежа.
  const backOccluders = useMemo(() => {
    if (!fitting.isSelfFacing || fitting.side !== 'back') return [];
    const angle = (Number(fitting.rotation) || 0) * Math.PI / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const originX = fitting.groupX != null ? Number(fitting.groupX) : x;
    const originY = fitting.groupY != null ? Number(fitting.groupY) : y;
    const offX = Number(fitting.offsetX) || 0;
    const offY = Number(fitting.offsetY) || 0;
    const toLocal = (wx, wy) => {
      const dx = wx - originX;
      const dy = wy - originY;
      return {
        x: dx * cos + dy * sin + offX - (x + contentOffsetX),
        y: -dx * sin + dy * cos + offY - (y + contentOffsetY),
      };
    };
    const worldBox = (item) => {
      const rot = (Number(item.rotation) || 0) * Math.PI / 180;
      const itemCos = Math.cos(rot);
      const itemSin = Math.sin(rot);
      const gx = Number(item.groupX) || 0;
      const gy = Number(item.groupY) || 0;
      const ox = Number(item.offsetX) || 0;
      const oy = Number(item.offsetY) || 0;
      const x0 = Number(item.x) || 0;
      const y0 = Number(item.y) || 0;
      const w = Number(item.width) || 0;
      const h = Number(item.height) || 0;
      const corners = [[x0, y0], [x0 + w, y0], [x0 + w, y0 + h], [x0, y0 + h]].map(([px, py]) => {
        const dx = px - ox;
        const dy = py - oy;
        return toLocal(gx + dx * itemCos - dy * itemSin, gy + dx * itemSin + dy * itemCos);
      });
      const left = Math.min(...corners.map((point) => point.x));
      const top = Math.min(...corners.map((point) => point.y));
      const right = Math.max(...corners.map((point) => point.x));
      const bottom = Math.max(...corners.map((point) => point.y));
      return { x: left, y: top, width: right - left, height: bottom - top };
    };
    const covers = [...(cylinders || []), ...(sceneFittings || []).filter((item) => (
      item
      && item.id !== fitting.id
      && !item.isHidden
      && !item.isSelfFacing
    ))];
    const raw = covers.map(worldBox).filter((rect) => (
      rect.width > 0.5
      && rect.height > 0.5
      && rect.x < width
      && rect.y < height
      && rect.x + rect.width > 0
      && rect.y + rect.height > 0
    ));
    return disjointOccluders(raw);
  }, [fitting, cylinders, sceneFittings, x, y, width, height, contentOffsetX, contentOffsetY]);

  // Фильтруем "масштабируемые" шейпы, "масштаб по X" и "немасштабируемые"
  // Используем processedShapes (после обрезки) вместо shapes
  const scalableShapes = processedShapes.filter((shape) => shape.scalable && !shape.scaleOnlyX);
  const xOnlyScalableShapes = processedShapes.filter((shape) => shape.scaleOnlyX);
  const nonScalableShapes = processedShapes.filter((shape) => !shape.scalable && !shape.scaleOnlyX);

  // Определяем цвет обводки (stroke)
  const strokeColor = (selectedFittingId === id || isPatternSelected) ? '#3f51b5' : 'rgba(0,0,0,0)';

  // При уходе мышки сбрасываем подсветку стороны
  const handleMouseOut = () => {
    setHighlightedSide(null);
  };

  const drawSymbol = (dash, opacity = 1) => (
    <React.Fragment>
      <Group scale={{ x: scaleX, y: scaleY }} listening={false}>
        {scalableShapes.map((shape, idx) => renderShape(shape, idx, dash, opacity))}
      </Group>
      <Group scale={{ x: scaleX, y: 1 }} listening={false}>
        {xOnlyScalableShapes.map((shape, idx) => renderShape(shape, idx, dash, opacity))}
      </Group>
      {nonScalableShapes.map((shape, idx) => renderShape(shape, idx, dash, opacity))}
    </React.Fragment>
  );

  return (
    <React.Fragment>
      {/**
       * Невидимый (полупрозрачный) прямоугольник, по клику на который мы выбираем фитинг.
       * По сути, аналогичный Rect, который у вас в Canvas рендерился для выбора.
       */}
      {/* Слой формы для взаимодействия (без заметной рамки) */}
      <Rect
        name="hitbox"
        id={id}
        x={x}
        y={y}
        width={width}
        height={height}
        fill="rgba(255, 255, 255, 0.5)"
        stroke={'rgba(0,0,0,0)'}
        strokeWidth={0}
        draggable={!locked}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onClick={(e) => {
          e.cancelBubble = true;
          setSelectedFittingId(id);
          if (typeof setSelectedCylinderId === 'function') setSelectedCylinderId(null);
          if (typeof setSelectedDimensionId === 'function') setSelectedDimensionId(null);
          if (typeof setSelectedBalloonId === 'function') setSelectedBalloonId(null);
          if (typeof onSelectFitting === 'function') onSelectFitting(id);
        }}
        {...(isDimensionLineEnabled
          ? {
              onMouseOver: handleMouseOver, 
              onMouseMove: handleDragMove,
              onMouseOut: handleMouseOut,
            }
          : {})}
      />

      {/**
       * Группа, в которой рендерим DXF-фигуры (scalableShapes, nonScalableShapes).
       * Позиционируем её в (x, y) с учетом возможного смещения для центрирования.
       */}
      <Group
        name={backOccluders.length ? 'fitting-visible' : undefined}
        clipMode={backOccluders.length ? 'visible' : undefined}
        clipRects={backOccluders.length ? backOccluders : undefined}
        clipBounds={backOccluders.length ? { x: 0, y: 0, width, height } : undefined}
        x={x + contentOffsetX}
        y={y + contentOffsetY}
        listening={false}
        clipFunc={backOccluders.length ? (ctx) => {
          ctx.beginPath();
          ctx.rect(0, 0, width, height);
          backOccluders.forEach((rect) => ctx.rect(rect.x, rect.y, rect.width, rect.height));
          ctx.clip('evenodd');
        } : undefined}
      >
        {drawSymbol()}
      </Group>
      {backOccluders.length > 0 && (
        <Group
          name="fitting-hidden"
          clipMode="hidden"
          clipRects={backOccluders}
          x={x + contentOffsetX}
          y={y + contentOffsetY}
          listening={false}
          clipFunc={(ctx) => {
            ctx.beginPath();
            backOccluders.forEach((rect) => ctx.rect(rect.x, rect.y, rect.width, rect.height));
            ctx.clip();
          }}
        >
          {drawSymbol([3, 2.5], 0.22)}
        </Group>
      )}

      {/** Иконка замка, если fitting.locked и зажата клавиша L */}
      {locked && isLKeyPressed && (
        <Text
          x={x + width / 2 - 8}
          y={y + height / 2 - 8}
          text="🔒"
          fontSize={16}
          fill="#000000"
          listening={false}
        />
      )}

      {/* Рамка выделения поверх всех фигур фитинга */}
      {(selectedFittingId === id || isPatternSelected) && (
        <Rect
          x={x}
          y={y}
          width={width}
          height={height}
          stroke={strokeColor}
          strokeWidth={2}
          listening={false}
        />
      )}

      {/**
       * Маркер изменения размера будет размещён ПОСЛЕ точек привязки и подсветки,
       * чтобы он был поверх и было удобно захватывать.
       */}

      {/**
       * Точки по периметру для привязки
       */}
      {(snappingMode === 'points' || pAttachmentMode === 'points') &&
        getFittingPoints({
          x,
          y,
          width: getFittingWidth(fitting),
          height: getFittingHeight(fitting),
          snapPoints: fitting.snapPoints,
          shapes: fitting.shapes,
        }).map((point, idx) => {
          // Проверяем, выбрана ли эта точка как первая в режиме привязки
          const isFirstPoint = pointAttachmentMode?.firstPoint && 
                               pointAttachmentMode.firstPoint.objectId === id && 
                               pointAttachmentMode.firstPoint.pointIndex === idx;
          
          const isConstrained = isPointConstrained && isPointConstrained(id, 'fitting', idx);
          
          const isInPointsMode = pAttachmentMode === 'points';
          
          return (
            <React.Fragment key={`${id}-point-${idx}-snap-group`}>
              <Circle
                key={`${id}-point-${idx}-snap`}
                x={point.x}
                y={point.y}
                radius={isInPointsMode ? 5 : 3} // Увеличиваем радиус в режиме привязки точек
                fill={isFirstPoint ? '#ff0000' : (isInPointsMode ? '#00ff00' : 'green')} // Красная если выбрана, ярко-зеленая в режиме привязки
                stroke={isInPointsMode ? '#ffffff' : undefined} // Белая обводка в режиме привязки
                strokeWidth={isInPointsMode ? 1 : 0}
                hitStrokeWidth={0}
                name="snap-point"
                listening
                onClick={(evt) => {
                  if (isInPointsMode && handleSnapPointClick) {
                    evt.cancelBubble = true; // Останавливаем всплытие события
                    
                    // Получаем абсолютные координаты точки на Stage с учетом трансформации
                    const circle = evt.target;
                    const absPos = circle.getAbsolutePosition();
                    
                    handleSnapPointClick(id, 'fitting', idx, absPos.x, absPos.y);
                  }
                }}
                onMouseDown={(evt) => {
                  if (isInPointsMode) {
                    // В режиме привязки точек не начинаем drag
                    evt.cancelBubble = true;
                    return;
                  }
                  if (!isDimensionLineEnabled) {
                    const group = evt.target.getParent();
                    if (group && typeof group.findOne === 'function') {
                      const rectNode = group.findOne('#' + id);
                      if (rectNode && typeof rectNode.startDrag === 'function') {
                        rectNode.startDrag();
                      }
                    }
                  }
                }}
                style={{ cursor: isInPointsMode ? 'pointer' : 'default' }}
              />
              
              {/* Визуальный индикатор привязки - показывается только в режиме привязки точек и если точка привязана */}
              {isInPointsMode && isConstrained && (
                <Group
                  x={point.x + 8}
                  y={point.y - 8}
                  onClick={(evt) => {
                    evt.cancelBubble = true;
                    if (handleRemoveConstraint) {
                      handleRemoveConstraint(id, 'fitting', idx);
                    }
                  }}
                  onMouseDown={(evt) => evt.cancelBubble = true}
                  listening
                  style={{ cursor: 'pointer' }}
                >
                  {/* Фон индикатора */}
                  <Circle
                    radius={6}
                    fill="#ffffff"
                    stroke="#ff9900"
                    strokeWidth={2}
                  />
                  {/* Иконка "звено цепи" */}
                  <Line
                    points={[-3, -2, -1, -2, -1, 0, 1, 0, 1, 2, 3, 2]}
                    stroke="#ff9900"
                    strokeWidth={1.5}
                    lineCap="round"
                    lineJoin="round"
                  />
                </Group>
              )}
            </React.Fragment>
          );
        })}

      {/* Визуализация стенок в режиме привязки стенок (показываем и для залоченных объектов) */}
      {pAttachmentMode === 'edges' && (() => {
        const edges = [
          { type: 'top', points: [x, y, x + width, y], midX: x + width / 2, midY: y },
          { type: 'bottom', points: [x, y + height, x + width, y + height], midX: x + width / 2, midY: y + height },
          { type: 'left', points: [x, y, x, y + height], midX: x, midY: y + height / 2 },
          { type: 'right', points: [x + width, y, x + width, y + height], midX: x + width, midY: y + height / 2 },
        ];

        const isFirstEdge = (localEdgeType) => {
          if (!pointAttachmentMode?.firstPoint?.isEdge || pointAttachmentMode.firstPoint.objectId !== id) {
            return false;
          }
          // Теперь в pointAttachmentMode хранится локальный тип, просто сравниваем
          return pointAttachmentMode.firstPoint.edgeType === localEdgeType;
        };

        return edges.map((edge) => {
          const isSelected = isFirstEdge(edge.type);
          // Для isConstrained используем локальный тип (constraints теперь хранят локальные типы)
          const isConstrained = isEdgeConstrained && isEdgeConstrained(id, 'fitting', edge.type);
          
          return (
            <React.Fragment key={`${id}-edge-${edge.type}`}>
              {/* Толстая невидимая линия для удобного клика */}
              <Line
                points={edge.points}
                stroke="rgba(0,0,0,0)"
                strokeWidth={20}
                hitStrokeWidth={20}
                listening
                onClick={(evt) => {
                  if (pAttachmentMode === 'edges' && handleEdgeClick) {
                    evt.cancelBubble = true;
                    const line = evt.target;
                    // Используем центр стенки
                    const centerX = edge.midX;
                    const centerY = edge.midY;
                    const group = line.getParent();
                    const t = group.getAbsoluteTransform();
                    const absCenter = t.point({ x: centerX, y: centerY });
                    // Передаем ЛОКАЛЬНЫЙ тип стенки (edge.type), чтобы он корректно работал при повороте объекта
                    handleEdgeClick(id, 'fitting', edge.type, absCenter.x, absCenter.y);
                  }
                }}
                onMouseOver={(evt) => {
                  evt.target.getStage().container().style.cursor = 'pointer';
                }}
                onMouseOut={(evt) => {
                  evt.target.getStage().container().style.cursor = 'default';
                }}
              />
              {/* Видимая линия для визуализации */}
              <Line
                points={edge.points}
                stroke={isSelected ? '#ff0000' : (isConstrained ? '#ffaa00' : '#00ff00')}
                strokeWidth={isSelected ? 3 : (isConstrained ? 2.5 : 2)}
                listening={false}
                dash={[5, 5]}
              />
              {/* Индикатор привязки стенки - показывается только если стенка привязана */}
              {isConstrained && (
                <Group
                  x={edge.midX}
                  y={edge.midY}
                  onClick={(evt) => {
                    evt.cancelBubble = true;
                    if (handleRemoveEdgeConstraint) {
                      handleRemoveEdgeConstraint(id, 'fitting', edge.type);
                    }
                  }}
                  onMouseDown={(evt) => evt.cancelBubble = true}
                  listening
                  style={{ cursor: 'pointer' }}
                >
                  {/* Фон индикатора */}
                  <Circle
                    radius={8}
                    fill="#ffffff"
                    stroke="#ffaa00"
                    strokeWidth={2}
                  />
                  {/* Иконка "звено цепи" */}
                  <Line
                    points={[-4, -2, -2, -2, -2, 0, 2, 0, 2, 2, 4, 2]}
                    stroke="#ffaa00"
                    strokeWidth={1.5}
                    lineCap="round"
                    lineJoin="round"
                  />
                </Group>
              )}
            </React.Fragment>
          );
        });
      })()}

      {isDimensionLineEnabled &&
        getFittingPoints({
          x,
          y,
          width: getFittingWidth(fitting),
          height: getFittingHeight(fitting),
          snapPoints: fitting.snapPoints,
          shapes: fitting.shapes,
          includeHidden: true, // Для размерных линий нужны ВСЕ точки (включая скрытые)
        }).map((point, idx) => (
          <Circle
            key={`${id}-point-${idx}-dim`}
            x={point.x}
            y={point.y}
            radius={5}
            fill={
              highlightedPoint &&
              highlightedPoint.objectType === 'fitting' &&
              highlightedPoint.objectId === id &&
              highlightedPoint.idx === idx
                ? 'red'
                : 'rgba(0,0,0,0)'
            }
            hitStrokeWidth={0}
            onMouseOver={(evt) => {
              const group = evt.target.getParent();
              const t = group.getAbsoluteTransform();
              const abs = t.point({ x: point.x, y: point.y });
              const z = zoom || 1;
              setHighlightedPoint({
                x: abs.x / z,
                y: abs.y / z,
                objectId: id,
                objectType: 'fitting',
                idx,
              });
            }}
            onMouseOut={() => {
              setHighlightedPoint(null);
            }}
          />
        ))}

      {/* Маркер изменения размера — в конце, поверх точек (виден даже при выделении) */}
      {!isDimensionLineEnabled && !isTransforming && !isPKeyPressed && !(fitting.isDiameterFixed === true && fitting.isLengthAdjustable === false) && fitting.type !== 'fastener' && (
        <Circle
          name="resize-handle"
          x={x + width}
          y={y + height}
          radius={5}
          fill="white"
          stroke="#000000"
          strokeWidth={2}
          draggable={!locked}
          dragBoundFunc={function (pos) {
            const group = this.getParent();
            const t = group.getAbsoluteTransform();
            const inv = t.copy().invert();
            const z = zoom || 1;
            const canAdjustLength = fitting.isLengthAdjustable !== false;
            const canAdjustWidth = fitting.isDiameterFixed === false;
            
            // Применяем привязку в мировых координатах Stage (до перевода в локальные)
            let finalPos = { x: pos.x, y: pos.y };
            if (typeof getSnappedPosition === 'function' && snappingMode === 'points') {
              const snapped = getSnappedPosition(pos.x / z, pos.y / z, id, width, height);
              finalPos = { x: snapped.x * z, y: snapped.y * z };
            }
            
            // Переводим в локальные координаты после привязки
            const local = inv.point(finalPos);
            
            // Проверка ограничений в локальных координатах
            if (!canAdjustWidth) {
              local.x = x + width;
            } else if (local.x < x + 20) {
              local.x = x + 20;
            }
            
            if (!canAdjustLength) {
              local.y = y + height;
            } else if (local.y < y + 20) {
              local.y = y + 20;
            }
            
            // Переводим обратно в абсолютные координаты
            const abs = t.point(local);
            return { x: abs.x, y: abs.y };
          }}
          onDragStart={handleResizeStart}
          onDragMove={(e) => handleFittingResize(e, fitting)}
          onDragEnd={handleResizeEnd}
          listening={true}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
        />
      )}

      {/**
       * Подсветка стороны, если highlightedSide.objectId === fitting.id
       */}
      {isDimensionLineEnabled &&
        highlightedSide &&
        highlightedSide.objectType === 'fitting' &&
        highlightedSide.objectId === id && (
          <Line
            points={
              highlightedSide.type === 'top'
                ? [x, y, x + getFittingWidth(fitting), y]
                : highlightedSide.type === 'bottom'
                ? [x, y + getFittingHeight(fitting), x + getFittingWidth(fitting), y + getFittingHeight(fitting)]
                : highlightedSide.type === 'left'
                ? [x, y, x, y + getFittingHeight(fitting)]
                : [x + getFittingWidth(fitting), y, x + getFittingWidth(fitting), y + getFittingHeight(fitting)]
            }
            stroke="#ff0000"
            strokeWidth={2}
          />
        )}
    </React.Fragment>
  );
}

export default FittingComponent;
